import React, { memo, useCallback, useMemo } from 'react';
import classnames from 'classnames';

import style from './checkbox.less';
import Noop from '../../default-props/noop';
import GuidService from '../../services/guid-service';

/**
 * @callback CheckboxChangeHandler
 * @param {boolean} checked
 * @param {React.ChangeEvent<HTMLInputElement>} event
 */

/**
 * @param {object} props
 * @param {string} [props.ariaLabel]
 * @param {string} [props.name]
 * @param {string} [props.inputId]
 * @param {string} [props.value]
 * @param {boolean} [props.disabled]
 * @param {boolean} props.checked
 * @param {CheckboxChangeHandler} props.onChange
 * @param {(event: React.MouseEvent<HTMLInputElement>) => void} [props.onClick]
 * @param {string} [props.label]
 * @param {boolean} [props.largeCheckbox]
 * @param {boolean} [props.indeterminate]
 * @param {string} [props.className]
 * @param {React.ReactNode|React.ReactNode[]} [props.children]
 * @param {string} [props.pendoTrackingId]
 * @param {number} [props.tabIndex]
 * @param {string} [props.innerClassname]
 * @returns {JSX.Element}
 */
const Checkbox = ({
  ariaLabel = undefined,
  name = '',
  inputId = undefined,
  value = '',
  disabled = false,
  checked,
  onClick = Noop,
  label = '',
  largeCheckbox = false,
  indeterminate = false,
  className = '',
  children,
  onChange,
  pendoTrackingId = undefined,
  tabIndex = -1,
  innerClassname,
}) => {
  const classesForContainer = classnames(
    style.checkbox,
    className,
    { [style.checked]: checked },
    { [style.disabled]: disabled },
    { [style['large-checkbox']]: largeCheckbox },
    { [style.indeterminate]: indeterminate },
    { [style['no-text']]: !label && !children },
  );
  const inputElementId = useMemo(() => inputId || `checkbox-${GuidService.newGuid()}`, [inputId]);

  const change = useCallback(
    (event) => {
      onChange(event.target.checked, event);
    },
    [onChange],
  );

  return (
    <div className={classesForContainer} tabIndex={tabIndex}>
      <input
        aria-label={ariaLabel}
        checked={checked}
        data-pendo-id={pendoTrackingId}
        disabled={disabled}
        id={inputElementId}
        name={name || inputElementId}
        onChange={change}
        type='checkbox'
        value={value}
      />
      <label htmlFor={inputElementId}>
        <span
          className={classnames(style['label-text'], innerClassname)}
          data-testid={`checkbox-clickable-${inputElementId}`}
          onClick={onClick}
        >
          <span>
            {label}
            {children}
          </span>
        </span>
      </label>
    </div>
  );
};

export default memo(Checkbox);
